import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { registerUnauthorizedHandler } from "../api/client"
import { type PortalAuthClient, logout as apiLogout } from "../api/endpoints/auth"
import { getProfile } from "../api/endpoints/portal"
import { setPortalLanguage } from "../i18n"

interface AuthContextValue {
  client: PortalAuthClient | null
  isLoading: boolean
  setSession: (client: PortalAuthClient, expiresAt: string) => void
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: PropsWithChildren) {
  const [client, setClient] = useState<PortalAuthClient | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const clearSession = useCallback(() => {
    setClient(null)
  }, [])

  const refresh = useCallback(async () => {
    try {
      const response = await getProfile()
      setClient({
        clientId: response.data.clientId,
        fullName: response.data.fullName,
        phoneNumber: response.data.phoneNumber,
        altPhone: response.data.altPhone,
        physicalAddress: response.data.physicalAddress,
        preferredLanguage: response.data.preferredLanguage,
        kycStatus: response.data.kycStatus,
      })
      setPortalLanguage(response.data.preferredLanguage ?? "en")
    } catch {
      clearSession()
    } finally {
      setIsLoading(false)
    }
  }, [clearSession])

  const setSession = useCallback((nextClient: PortalAuthClient, _expiresAt: string) => {
    setClient(nextClient)
    setPortalLanguage(nextClient.preferredLanguage ?? "en")
  }, [])

  const logout = useCallback(async () => {
    try {
      await apiLogout()
    } catch {
      // Clear local state even if the server session has already expired.
    } finally {
      clearSession()
    }
  }, [clearSession])

  useEffect(() => {
    registerUnauthorizedHandler(() => {
      clearSession()
    })

    refresh()
    return () => registerUnauthorizedHandler(null)
  }, [clearSession, refresh])

  return (
    <AuthContext.Provider value={{ client, isLoading, setSession, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider")
  }

  return context
}
